import React, { createContext, useContext, useEffect, useState } from 'react';

export type ThemePreference = 'system' | 'light' | 'dark';

interface ThemeContextType {
  preference: ThemePreference;
  setPreference: (p: ThemePreference) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

function resolveEffective(preference: ThemePreference): 'light' | 'dark' {
  if (preference !== 'system') return preference;
  return window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
}

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [preference, setPreferenceState] = useState<ThemePreference>(
    () => (localStorage.getItem('theme') as ThemePreference) ?? 'system'
  );

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', resolveEffective(preference));

    if (preference === 'system') {
      const mq = window.matchMedia('(prefers-color-scheme: light)');
      const handler = () => {
        document.documentElement.setAttribute('data-theme', resolveEffective('system'));
      };
      mq.addEventListener('change', handler);
      return () => mq.removeEventListener('change', handler);
    }
  }, [preference]);

  const setPreference = (p: ThemePreference) => {
    localStorage.setItem('theme', p);
    setPreferenceState(p);
  };

  return (
    <ThemeContext.Provider value={{ preference, setPreference }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
};
